import { c as _c } from "react/compiler-runtime";
import { Button, Chip, ChipGroup, InputWrapper, Menu, Switch, TextInput, TextInputProps, Tooltip, useSafeMantineTheme } from '@mantine/core';
import { QuestionCfg, QuestionForecastType, ResolutionCfg, ResolutionValueAny, SubqCfg, zResolutionCfg, ResolutionValue } from 'shared/forecast';
import { Resolution as R } from 'shared/forecast/resolution';
import { isNullOrEmpty } from 'shared/collection';
import { notNull } from 'shared/type';
import { pathValueDynamic } from 'shared/paths';
import { fmtNum } from 'shared/format';
import { LabelEmptyList, LabelForm } from 'shared/ui';
import { UseDbForm } from 'components/hooks/useDbForm';
import { RichText } from 'components/form/RichText';
import { OverflowMenu } from 'components/Menu';
import { openConfirmModal } from '@mantine/modals';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleProps } from 'components';
import { HiTrash } from 'react-icons/hi2';
import { HiPlusCircle } from 'react-icons/hi';
import { MdOutlineUndo } from 'react-icons/md';
import { nanoid } from 'nanoid';
import { OrderableList } from 'components/form/OrderableList';
import { entries, keys, mapToObj, pipe } from 'remeda';
import { O } from 'ts-toolbelt';
import { SColorInput } from 'components/form/SColorInput';
import { match } from 'ts-pattern';
import { twMerge } from 'tailwind-merge';
import { StringNumberInput } from 'components/form/StringNumberInput';
import { DateTime } from 'luxon';

// type QuestionCfgForResolutions = Pick<QuestionCfg | SubquestionCfg, 'forecastType' | 'resolutions'>
type ResolutionsEditMode = 'question-consequence-resolutions' | 'consequence-resolutions' | 'subquestion';
export type ResolutionsEditProps = {
  mode: ResolutionsEditMode;
  parentQ: R.ParentQuestionForResolutions;
  simplified?: boolean;
  qForm: Pick<UseDbForm<Pick<SubqCfg, 'resolutions' | 'consequenceResolutions'> | Pick<QuestionCfg, 'resolutions'>>, 'setFieldValueDynamic' | 'setValues' | 'getInputProps' | 'values'>;
  debugName?: string;
} & StyleProps;
export const ResolutionsEdit = ({
  mode,
  parentQ,
  qForm,
  className,
  simplified,
  debugName
}: ResolutionsEditProps) => {
  const [focusedResolutionId, setFocusedResolutionId] = useState<string>();
  const subquestion = mode == 'subquestion' ? qForm.values as SubqCfg : undefined;
  const subquestionType = subquestion?.subquestionType ?? 'consequence';
  const forecastType = match(mode) // resolutions are stored keyed by forecast type appropriate.
  .with('subquestion', () =>
  // for subquestions this is it's forecast type, unless its a consequence then it the type of the parent
  subquestion!.subquestionType == 'consequence' ? parentQ.forecastType : subquestion!.forecastType).otherwise(() => parentQ.forecastType);
  const consequenceResolutions = match(mode).with('subquestion', () => parentQ.consequenceResolutions) // not editing, just use as passed in
  .with('question-consequence-resolutions', () => (qForm.values as QuestionCfg).resolutions) // editing the question resolutions, use the form
  .with('consequence-resolutions', () => (qForm.values as SubqCfg).consequenceResolutions).exhaustive();
  const {
    resolutions: resolutions_0,
    resolutionItems: resolutionItems_0
  } = useMemo(() => {
    const resolutions = R.resolutions({
      forecastType: parentQ.forecastType,
      consequenceResolutions
    }, mode == 'subquestion' ? qForm.values as SubqCfg : undefined);
    const resolutionItems = resolutions.map(r => ({
      ...r,
      position: r.resolution.position ?? 0
    }));
    return {
      resolutions,
      resolutionItems
    };
  }, [consequenceResolutions, parentQ.forecastType, qForm.values]);
  const resolutionsPath = (id?: string, ...path: string[]) => [match(mode).with('subquestion', () => 'resolutions').with('question-consequence-resolutions', () => 'resolutions') // this should have been called consequenceResolutions like the subq. But for backwards compatpatibility i'm leaving it
  .with('consequence-resolutions', () => 'consequenceResolutions').exhaustive(), forecastType, id, ...path].filter(notNull);

  // sync consequence resolutions with new question resolutions by adding or removing overrides accordingly
  useEffect(() => {
    if (mode != 'subquestion') return;
    const needsNew = resolutions_0.filter(r_0 => r_0.newOverride);
    if (needsNew.length) qForm.setValues(mapToObj(needsNew, r_1 => [resolutionsPath(r_1.id).join('.'), r_1.resolution]));
  }, [mode, JSON.stringify({
    parentQ,
    values: consequenceResolutions
  })]);

  // sync defaults for fresh resolutions
  useEffect(() => {
    if (qForm.values == null) return;
    const rValues = pathValueDynamic(qForm.values, resolutionsPath());
    if (rValues == null) {
      const newResolutions = match(mode).with('question-consequence-resolutions', 'consequence-resolutions', () => R.defaultQuestionResolutions(forecastType)).with('subquestion', () => R.defaultSubquestionResolutions({
        questionForecastType: parentQ.forecastType,
        subquestionForecastType: subquestion!.forecastType,
        subquestionType: subquestionType
      })).exhaustive();
      if (isNullOrEmpty(newResolutions)) return;
      qForm.setFieldValueDynamic(resolutionsPath(), newResolutions, 'freshResolutions');
    }
  }, [qForm.values]);
  const resProps = (props: Omit<GetEditProps, 'type' | 'qForm'>) => pipe(getEditProps({
    ...props,
    qForm
  }), p => ({
    ...p,
    simplified
  }));
  const rootRes = subquestion && subquestion[subquestion.forecastType] ? resProps({
    path: [subquestion.forecastType],
    item: {
      resolution: subquestion[subquestion.forecastType]
    } as R.ResolutionItem
  }) : undefined;
  return <div className={twMerge(`max-w-[80em] space-y-4`, className)}>
      {rootRes && <div>
          <LabelForm className=''>Forecast this question</LabelForm>
          <ResolutionEdit className='mt-2' {...rootRes} />
        </div>}
      <div>
        <div className='flex w-full items-center justify-between'>
          <LabelForm>
            {subquestion == null ? 'Default question resolutions for consequences' : match(subquestionType).with('cause', () => 'Forecast main question conditional on how this resolves:').with('related', () => 'Additional variations of this question').with('consequence', () => 'Forecast this question conditional on how the main question resolves:').exhaustive()}
          </LabelForm>
          {!simplified && <Button leftSection={<HiPlusCircle />} onClick={() => {
          const id_0 = nanoid(6);
          qForm.setFieldValueDynamic(resolutionsPath(id_0), zResolutionCfg.parse({
            position: resolutions_0.length,
            enableForecasts: true
          } as Partial<ResolutionCfg>));
          setFocusedResolutionId(id_0);
        }}>
              Resolution
            </Button>}
        </div>

        <div className={twMerge('flex w-full flex-col mt-2', simplified ? 'gap-1' : 'gap-6')}>
          {simplified ? resolutionItems_0.map(r_2 => <ResolutionEdit key={r_2.id} {...resProps({
          item: r_2 as R.ResolutionItem,
          path: resolutionsPath(r_2.id)
        })} />) : resolutionItems_0.length == 0 ? <LabelEmptyList>no resolutions yet...</LabelEmptyList> : <OrderableList onReorder={({
          positions
        }) => {
          qForm.setValues(mapToObj(positions, d => [resolutionsPath(d.id, 'position').join('.'), d.position]));
        }} data={resolutionItems_0} component={r_3 => {
          const {
            id: id_1
          } = r_3;
          return <ResolutionEdit key={id_1} showDelete autoFocus={id_1 == focusedResolutionId} className='p-2' {...resProps({
            item: r_3 as R.ResolutionItem,
            path: resolutionsPath(id_1)
          })} />;
        }} />}
        </div>
      </div>
    </div>;
};
type GetInputPropsType = 'input' | 'checkbox';
type ResolutionEditProps = {
  item: O.Optional<Pick<R.ResolutionItem, 'resolution' | 'resolutionDisplay' | 'resolutionParent'>, 'resolutionDisplay'>;
  inputProps: (prop: keyof ResolutionCfg, type?: GetInputPropsType) => GetInputProps;
  setFieldValue: <K extends keyof ResolutionCfg>(prop: K, value: ResolutionCfg[K] | undefined) => void;
  setValue: (v: ResolutionValueAny) => void;
  path: string[];
};
type GetInputProps = Partial<Pick<TextInputProps, 'onChange' | 'disabled'>> & {
  value?: string;
};
type GetEditProps = {
  item: ResolutionEditProps['item'];
  path: string[];
  qForm: Pick<UseDbForm<SubqCfg | QuestionCfg>, 'getInputProps' | 'setFieldValueDynamic'>;
};
const getEditProps = ({
  item,
  path,
  qForm
}: GetEditProps): ResolutionEditProps => {
  const {
    resolution
  } = item;
  const resolutionDisplay = item.resolutionDisplay ?? resolution as ResolutionCfg;
  const propPath = (prop: keyof ResolutionCfg) => [...path, prop];
  const isOverride = R.isOverride(resolution);
  const inputProps = (prop: keyof ResolutionCfg, type?: GetInputPropsType): GetInputProps => isOverride && !R.isOverrideProp(prop) ? {
    value: resolutionDisplay[prop]?.toString(),
    disabled: true
  } : qForm.getInputProps(propPath(prop).join('.'), {
    type
  });
  const setFieldValue = <T extends keyof ResolutionCfg,>(prop: T, value: ResolutionCfg[T] | undefined) => {
    if (!isOverride || ['enableForecasts'].includes(prop)) qForm.setFieldValueDynamic(propPath(prop), value);
  };
  const setValue = (v: ResolutionValueAny) => qForm.setFieldValueDynamic(path, v);
  return {
    item,
    inputProps,
    setFieldValue,
    setValue,
    path
  };
};
export const ResolutionEdit = t0 => {
  const $ = _c(59);
  let autoFocus;
  let className;
  let hideLabelEditing;
  let inputProps;
  let item;
  let props;
  let setFieldValue;
  let setValue;
  let simplified;
  if ($[0] !== t0) {
    const {
      item: t1,
      path,
      inputProps: t2,
      setFieldValue: t3,
      setValue: t4,
      autoFocus: t5,
      hideLabelEditing: t6,
      simplified: t7,
      className: t8,
      ...t9
    } = t0;
    item = t1;
    inputProps = t2;
    setFieldValue = t3;
    setValue = t4;
    autoFocus = t5;
    hideLabelEditing = t6;
    simplified = t7;
    className = t8;
    props = t9;
    $[0] = t0;
    $[1] = autoFocus;
    $[2] = className;
    $[3] = hideLabelEditing;
    $[4] = inputProps;
    $[5] = item;
    $[6] = props;
    $[7] = setFieldValue;
    $[8] = setValue;
    $[9] = simplified;
  } else {
    autoFocus = $[1];
    className = $[2];
    hideLabelEditing = $[3];
    inputProps = $[4];
    item = $[5];
    props = $[6];
    setFieldValue = $[7];
    setValue = $[8];
    simplified = $[9];
  }
  const {
    resolution,
    resolutionParent
  } = item;
  const resolutionDisplay = item.resolutionDisplay ?? resolution as ResolutionCfg;
  const {
    colors
  } = useSafeMantineTheme();
  let t1;
  if ($[10] !== resolution) {
    t1 = R.isOverride(resolution);
    $[10] = resolution;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const isOverride = t1;
  let t2;
  if ($[12] !== resolution || $[13] !== resolutionParent) {
    t2 = resolutionParent != null && R.isStandard(resolution);
    $[12] = resolution;
    $[13] = resolutionParent;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const isConflict = t2;
  let t3;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      input: "!font-semibold !text-[1rem]"
    };
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== isOverride) {
    t4 = {
      variant: "title",
      classNames: t3,
      readOnly: isOverride
    };
    $[16] = isOverride;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const headerInputCommon = t4 satisfies Partial<TextInputProps>;
  let t5;
  if ($[18] !== setFieldValue) {
    t5 = md => {
      setFieldValue("description", md);
    };
    $[18] = setFieldValue;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const onDescriptionChange = t5;
  const showDelete = props.showDelete && !isOverride;
  const t6 = `@container @[40em]:gap-4 flex w-full items-center gap-2 ${className}`;
  const t7 = `forecasting ${resolutionDisplay.enableForecasts ? "enabled" : "disabled"}`;
  let t8;
  if ($[20] !== setFieldValue) {
    t8 = v => {
      setFieldValue("enableForecasts", v.target.checked);
    };
    $[20] = setFieldValue;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== resolutionDisplay.enableForecasts || $[23] !== t8) {
    t9 = <div><Switch checked={resolutionDisplay.enableForecasts} onChange={t8} /></div>;
    $[22] = resolutionDisplay.enableForecasts;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t9) {
    t10 = <Tooltip label={t7}>{t9}</Tooltip>;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== autoFocus || $[29] !== colors || $[30] !== headerInputCommon || $[31] !== hideLabelEditing || $[32] !== inputProps || $[33] !== isOverride || $[34] !== onDescriptionChange || $[35] !== resolutionDisplay.description || $[36] !== resolutionDisplay.label || $[37] !== simplified) {
    t11 = !hideLabelEditing && <div className="flex w-full flex-col gap-1 rounded-lg" style={{
      backgroundColor: isOverride && !simplified ? colors.gray[1] : ""
    }}>{simplified && isOverride ? <div className="font-semibold">{resolutionDisplay.label}</div> : <TextInput placeholder="title" {...headerInputCommon} {...inputProps("label")} autoFocus={autoFocus} />}{!simplified && <><div className="flex items-center gap-2"><p className="shrink-0 grow-0 text-sm text-gray-400">(short)</p><TextInput {...headerInputCommon} className="grow" placeholder="short title (for viz)" {...inputProps("labelShort")} /></div><RichText className="max-w-text" style={isOverride ? {
          color: colors.gray[4]
        } : {}} value={resolutionDisplay.description ?? ""} readonly={isOverride} syncValue={true} placeholder="Notes, context and further clarifications..." onChange={onDescriptionChange} /></>}</div>;
    $[28] = autoFocus;
    $[29] = colors;
    $[30] = headerInputCommon;
    $[31] = hideLabelEditing;
    $[32] = inputProps;
    $[33] = isOverride;
    $[34] = onDescriptionChange;
    $[35] = resolutionDisplay.description;
    $[36] = resolutionDisplay.label;
    $[37] = simplified;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== inputProps || $[40] !== simplified) {
    t12 = !simplified && <SColorInput className="@[40em]:w-[9em] w-[4em]" placeholder="color" {...inputProps("color") as any} />;
    $[39] = inputProps;
    $[40] = simplified;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== resolutionDisplay.label || $[43] !== setFieldValue || $[44] !== showDelete) {
    t13 = showDelete && <Menu.Item leftSection={<HiTrash />} onClick={() => {
      openConfirmModal({
        title: "Delete conditional resolution?",
        centered: true,
        children: <p>Are you sure you want to delete conditional resolution: <b>{resolutionDisplay.label}</b>? If there are any forecasts for this condition, they will no longer be available.</p>,
        labels: {
          confirm: "Delete",
          cancel: "Cancel"
        },
        confirmProps: {
          color: "red",
          variant: "filled"
        },
        onConfirm: () => setFieldValue("deleted", true)
      });
    }}>Delete</Menu.Item>;
    $[42] = resolutionDisplay.label;
    $[43] = setFieldValue;
    $[44] = showDelete;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== isConflict || $[47] !== resolution || $[48] !== setValue) {
    t14 = isConflict && <Menu.Item leftSection={<MdOutlineUndo />} onClick={() => setValue({
      type: "override",
      position: resolution.position
    })}>Reset to question version</Menu.Item>;
    $[46] = isConflict;
    $[47] = resolution;
    $[48] = setValue;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== t13 || $[51] !== t14) {
    t15 = <div><OverflowMenu width={200}>{t13}{t14}</OverflowMenu></div>;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t12 || $[56] !== t15 || $[57] !== t6) {
    t16 = <div className={t6}>{t10}{t11}{t12}{t15}</div>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t15;
    $[57] = t6;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  return t16;
};
type ResolutionValueProps = ResolutionValue & {
  type: QuestionForecastType;
  onChange: (v: ResolutionValue) => void;
  className?: string;
  unitLabel?: string;
};
const resMd = {
  likelihood: {
    pending: {
      desc: 'Resolution pending'
    },
    true: {
      desc: 'Resolved true'
    },
    false: {
      desc: 'Resolved false'
    },
    'n/a': {
      desc: "This condition is no longer applicable. e.g. it is conditional on something that didn't happen"
    }
  },
  amount: {
    pending: {
      desc: 'Resolution pending'
    },
    value: {
      desc: 'Resolved value'
    },
    'n/a': {
      desc: "This condition is no longer applicable. e.g. it is conditional on something that didn't happen"
    }
  }
} as const;
export const ResolutionValueEdit = t0 => {
  const $ = _c(46);
  const {
    status,
    num,
    note,
    type,
    label,
    statusUpdated,
    unitLabel,
    onChange,
    className
  } = t0;
  let t1;
  if ($[0] !== label || $[1] !== note || $[2] !== num || $[3] !== onChange || $[4] !== status || $[5] !== statusUpdated) {
    t1 = v => {
      if (v.status != status) {
        v.statusUpdated = DateTime.now().toISO();
      }
      return onChange({
        status,
        num,
        note,
        label,
        statusUpdated,
        ...v
      });
    };
    $[0] = label;
    $[1] = note;
    $[2] = num;
    $[3] = onChange;
    $[4] = status;
    $[5] = statusUpdated;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const onCh = t1;
  const statusMd = resMd[type];
  let t2;
  if ($[7] !== className) {
    t2 = twMerge("flex flex-col gap-4", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== onCh || $[10] !== statusMd) {
    t3 = v_0 => onCh({
      status: keys(statusMd).includes(v_0 as any) ? v_0 as ResolutionValueProps['status'] : "pending",
      label: "",
      num: undefined
    });
    $[9] = onCh;
    $[10] = statusMd;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = keys(statusMd).includes(status as any) ? status : "pending";
  let t5;
  if ($[12] !== type) {
    t5 = entries(resMd[type]).map(_temp9);
    $[12] = type;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <InputWrapper label="Resolved status"><div className="flex gap-2"><ChipGroup onChange={t3} value={t4}>{t5}</ChipGroup></div></InputWrapper>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== label || $[19] !== num || $[20] !== onCh || $[21] !== status || $[22] !== type || $[23] !== unitLabel) {
    let t8;
    if ($[25] !== label || $[26] !== onCh || $[27] !== status) {
      t8 = () => match(status).with("false", "true", "n/a", () => <TextInput className="w-[8em]" label="Custom Label" value={label} onChange={e => onCh({
        label: e.target.value
      })} />).otherwise(_temp10);
      $[25] = label;
      $[26] = onCh;
      $[27] = status;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    let t9;
    if ($[29] !== num || $[30] !== onCh || $[31] !== status || $[32] !== unitLabel) {
      t9 = () => match(status).with("value", () => <div className="flex gap-2 items-end"><StringNumberInput className="w-[6em]" label="value" fmt={_temp11} value={num} onChange={v_3 => onCh({
          num: v_3
        })} />{unitLabel ? <div className="pb-1">{unitLabel}</div> : <></>}</div>).otherwise(_temp12);
      $[29] = num;
      $[30] = onCh;
      $[31] = status;
      $[32] = unitLabel;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    t7 = match(type).with("likelihood", t8).with("amount", t9).exhaustive();
    $[18] = label;
    $[19] = num;
    $[20] = onCh;
    $[21] = status;
    $[22] = type;
    $[23] = unitLabel;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[34] !== t6 || $[35] !== t7) {
    t8 = <div className="flex gap-8">{t6}{t7}</div>;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  const t9 = note ?? "";
  let t10;
  if ($[37] !== onCh) {
    t10 = v_4 => onCh({
      note: v_4
    });
    $[37] = onCh;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t9) {
    t11 = <RichText value={t9} syncValue={true} placeholder="Notes, context and further clarifications about resolution..." onChange={t10} />;
    $[39] = t10;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== t11 || $[43] !== t2 || $[44] !== t8) {
    t12 = <div className={t2}>{t8}{t11}</div>;
    $[42] = t11;
    $[43] = t2;
    $[44] = t8;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  return t12;
};
function _temp9(t0) {
  const [k, v_1] = t0;
  return <Tooltip label={v_1.desc}><div><Chip value={k}>{k}</Chip></div></Tooltip>;
}
function _temp10() {
  return <></>;
}
function _temp11(v_2) {
  return fmtNum(v_2, "a");
}
function _temp12() {
  return <></>;
}